import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  SHIPPING_ADDRESS_ERROR,
  SHIPPING_METHOD_ERROR,
  BILLING_ADDRESS_ERROR,
  ITEMS_NOT_DELIVERABLE_ERROR,
  ITEMS_NOT_DELIVERABLE_CART_ERROR,
  PAYMENT_ERROR,
  PAYMENT_SESSION_AUTHORIZATION_ERROR,
  CARD_TOKEN_ERROR,
  PAYMENT_DATA_NOT_COMPLETE_ERROR,
  CARD_TOKENIZATION_ERROR,
  ORDER_FAILED,
  ORDER_FRAUD_ERROR,
  AMAZON_ERROR,
  SHIPPING_ADDRESS_UNEXPECTED_ERROR,
  SHIPPING_ADDRESS_STATE_NOT_ALLOWED,
  BILLING_ADDRESS_UNEXPECTED_ERROR,
  GIFT_CARD_ONLY_PAYMENT_ERROR,
  PAYMENT_ERROR_DECLINED_GIFT_CARD,
  SHIPPING_INFO_STEP,
  DELIVERY_METHODS_STEP,
  PAYMENT_STEP,
  TRUEMED_PAYMENT_ERROR,
} from '../../blocks/Checkout/SteppedCheckout/constants';
import { scrollToError } from '../../utils';
import getCheckoutErrorMessage from '../../utils/checkout/checkout';

const shippingErrorCodes = [
  SHIPPING_ADDRESS_ERROR,
  SHIPPING_ADDRESS_UNEXPECTED_ERROR,
  SHIPPING_ADDRESS_STATE_NOT_ALLOWED,
];
const deliveryErrorCodes = [
  SHIPPING_METHOD_ERROR,
  ITEMS_NOT_DELIVERABLE_ERROR,
  ITEMS_NOT_DELIVERABLE_CART_ERROR,
];
const paymentErrorCodes = [
  BILLING_ADDRESS_ERROR,
  PAYMENT_ERROR,
  PAYMENT_SESSION_AUTHORIZATION_ERROR,
  CARD_TOKEN_ERROR,
  PAYMENT_DATA_NOT_COMPLETE_ERROR,
  CARD_TOKENIZATION_ERROR,
  ORDER_FAILED,
  ORDER_FRAUD_ERROR,
  AMAZON_ERROR,
  BILLING_ADDRESS_UNEXPECTED_ERROR,
  GIFT_CARD_ONLY_PAYMENT_ERROR,
  PAYMENT_ERROR_DECLINED_GIFT_CARD,
  TRUEMED_PAYMENT_ERROR,
];

const isShippingStepError = (errorCode) =>
  shippingErrorCodes.includes(errorCode);

const isDeliveryMethodStepError = (errorCode) =>
  deliveryErrorCodes.includes(errorCode);

const isPaymentMethodStepError = (errorCode) =>
  paymentErrorCodes.includes(errorCode);

const isOtherError = (errorCode) =>
  !!errorCode &&
  !isShippingStepError(errorCode) &&
  !isDeliveryMethodStepError(errorCode) &&
  !isPaymentMethodStepError(errorCode);

const getStep = (errorCode) => {
  if (isShippingStepError(errorCode)) {
    return SHIPPING_INFO_STEP;
  }
  if (isDeliveryMethodStepError(errorCode)) {
    return DELIVERY_METHODS_STEP;
  }
  if (isPaymentMethodStepError(errorCode)) {
    return PAYMENT_STEP;
  }
  return undefined;
};

const useSteppedCheckoutError = () => {
  const { errorCode: orderErrorCode } = useSelector((state) => state.order);

  const { errorCode: paymentErrorCode } = useSelector(
    (state) => state.payments,
  );
  const { shippingAddressErrorCode, billingAddressErrorCode } = useSelector(
    (state) => state.cart,
  );
  const { errorCode: shippingMethodErrorCode } = useSelector(
    (state) => state.shippingMethods,
  );
  const dispatch = useDispatch();

  const [errorCode, setErrorCode] = useState(undefined);

  useEffect(() => {
    if (orderErrorCode) {
      console.log({
        title: 'CheckoutError',
        message: `orderErrorCode - ${orderErrorCode}`,
      });
    }
    if (shippingMethodErrorCode) {
      console.log({
        title: 'CheckoutError',
        message: `shippingMethodErrorCode - ${shippingMethodErrorCode}`,
      });
    }
    if (shippingMethodErrorCode) {
      console.log({
        title: 'CheckoutError',
        message: `shippingMethodErrorCode - ${shippingMethodErrorCode}`,
      });
    }
    if (shippingAddressErrorCode) {
      console.log({
        title: 'CheckoutError',
        message: `shippingAddressErrorCode - ${shippingAddressErrorCode}`,
      });
    }
    if (billingAddressErrorCode) {
      console.log({
        title: 'CheckoutError',
        message: `billingAddressErrorCode - ${billingAddressErrorCode}`,
      });
    }
    setErrorCode(
      orderErrorCode ??
        paymentErrorCode ??
        shippingMethodErrorCode ??
        shippingAddressErrorCode ??
        billingAddressErrorCode,
    );
  }, [
    orderErrorCode,
    paymentErrorCode,
    shippingAddressErrorCode,
    billingAddressErrorCode,
    shippingMethodErrorCode,
  ]);

  useEffect(() => {
    if (errorCode) {
      const step = getStep(errorCode);
      console.log({
        title: 'CheckoutError',
        message: JSON.stringify({
          Error: getCheckoutErrorMessage(errorCode),
          ErrorCode: errorCode,
          ErrorStep: getStep(errorCode),
          isDeliveryMethodStepError: isDeliveryMethodStepError(errorCode),
          isOtherError: isOtherError(errorCode),
          isPaymentMethodStepError: isPaymentMethodStepError(errorCode),
          isShippingError: isShippingStepError(errorCode),
        }),
        encodeMessageInBase64: true,
      });

      if (step) {
        dispatch({
          type: 'setEditingStep',
          value: getStep(errorCode),
        });
        window.heap?.track('CheckoutError', {
          Error: getCheckoutErrorMessage(errorCode),
          ErrorCode: errorCode,
          ErrorStep: getStep(errorCode),
          isDeliveryMethodStepError: isDeliveryMethodStepError(errorCode),
          isOtherError: isOtherError(errorCode),
          isPaymentMethodStepError: isPaymentMethodStepError(errorCode),
          isShippingError: isShippingStepError(errorCode),
        });
      } else {
        scrollToError('general');
      }
    }
  }, [errorCode, dispatch]);

  return {
    errorCode,
    errorMessage: errorCode ? getCheckoutErrorMessage(errorCode) : undefined,
    isShippingStepError: isShippingStepError(errorCode),
    isDeliveryMethodStepError: isDeliveryMethodStepError(errorCode),
    isPaymentMethodStepError: isPaymentMethodStepError(errorCode),
    isOtherError: isOtherError(errorCode),
  };
};

export default useSteppedCheckoutError;
